/* eslint-disable react-hooks-ext/exhaustive-deps -- TODO(react-hooks): 1 hook(s) need dependency review; fix each and remove this directive */
import { useContext, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useAppDispatch as useDispatch } from "@Reducers/hooks";
import { saveCommentsData } from "@Features/issues/state/bcfData.actions";
import { cubeView, getCubeViewSnapshot } from "../../services/cubeView";
import cloneObject from "../../services/cloneObject";
import ButtonComponent from "../../cubeComponent/form/ButtonComponent";
import { BUTTON_TYPES } from "../../cubeComponent/constants/globalVariable";
import { CubeGlobal, HoopsViewerContext } from "@Features/viewer-core";
import UserNameTagComponent from "../../cubeComponent/html/UserNameTagComponent";
import { useTranslation } from "@i18n";
import { SimpleEditor } from "../../cubeComponent/RichTextEditor/components/tiptap-templates/simple/ControlledSimpleEditor";
import { extractTempKeysFromHtml, replaceBase64WithTempKeys } from "@Helpers/htmlUtils";
import { getBackendUrl } from "@Utils";
import { uploadStringToTemp } from "../../services/tempUpload";
import Text from "@CubeComponents/html/Text/Text";
import { Bcf } from "@ts3d-hoops/web-viewer";
import { RootState } from "@Reducers/index";
import { objectIdString } from "@Helpers/schemas/common.schema";

/**
 * Rendered only by `CommentsList`, which forwards its own props straight through; `issueId` arrives as
 * a plain string and is parsed against `objectIdString` at the request boundary below.
 */
type CommentsAddProps = {
  issueId: string;
  fileVersionId?: string;
  issueIndex?: number;
  handleAddComment?: (issueIndex?: number) => void;
  handelShowAddComment: (show: boolean) => void;
  pdfViewer?: boolean;
  issueIsStandalone?: boolean;
};

function CommentsAdd(props: CommentsAddProps) {
  const { t } = useTranslation();
  const { viewerImpl } = useContext(HoopsViewerContext);
  const dispatch = useDispatch();

  const [commentTitle, setCommentTitle] = useState("");
  const [commentSnapshot, setCommentSnapshot] = useState("");
  const [commentViewpoint, setCommentViewpoint] = useState("");
  const [bcfData, setBcfData] = useState("");
  const userDetails = useSelector((state: RootState) => state.userDetails);
  const [postBtnEnabled, setPostBtnEnabled] = useState(false);

  const firstNameRaw = userDetails.firstName ?? "";
  const lastNameRaw = userDetails.lastName;
  const createdByName = {
    firstName: firstNameRaw.charAt(0).toUpperCase() + firstNameRaw.slice(1).toLowerCase(),
    lastName: lastNameRaw ? lastNameRaw.charAt(0).toUpperCase() + lastNameRaw.slice(1).toLowerCase() : "",
  };

  const getUserAvatar = (id?: string) => {
    return <UserNameTagComponent showAvatarOnly id={id} size="LSM" isCircle />;
  };

  const handleIssueViewPoint = async () => {
    // `BCFViewpoint._viewer` is a `private readonly` back-reference to the viewer that neither
    // `cloneObject` nor `JSON.stringify` can walk, so it is stripped first. The class does not expose
    // the field, hence the erased view of it at the delete.
    let bcfDataTemp: object = await Bcf.BCFViewpoint.createViewpoint(CubeGlobal.hwv, "viewpoint.bcfv");
    delete (bcfDataTemp as { _viewer?: unknown })._viewer;
    bcfDataTemp = cloneObject(bcfDataTemp);
    setBcfData(JSON.stringify(bcfDataTemp));

    const viewpoint = await cubeView.toJson(viewerImpl);
    setCommentViewpoint(JSON.stringify(viewpoint));
  };

  const handleTakeSnapshot = async () => {
    const snapshot = await getCubeViewSnapshot();

    // Upload snapshot to temp storage
    try {
      const base64Data = snapshot.split(",")[1];
      const byteCharacters = atob(base64Data);
      const byteArray = new Uint8Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteArray[i] = byteCharacters.charCodeAt(i);
      }
      const blob = new Blob([byteArray], { type: "image/png" });
      const tempKey = await uploadStringToTemp(blob, "snapshot.png");
      setCommentSnapshot(tempKey || snapshot);
    } catch {
      setCommentSnapshot(snapshot);
    }

    handleIssueViewPoint();
  };

  const handleCommentDescriptionChange = (value: string) => {
    setCommentTitle(value);
    if (value.length > 0) {
      const doc = new DOMParser().parseFromString(value, "text/html");
      const text = doc.body.textContent || "";
      const hasImage = doc.body.querySelector("img") !== null;
      const isUploading = doc.body.querySelector('[data-type="image-upload"]') !== null;
      setPostBtnEnabled((text.trim() !== "" || hasImage) && !isUploading);
    } else {
      setPostBtnEnabled(false);
    }
  };

  const handleCancelComment = () => {
    props.handleAddComment!(props.issueIndex);
    props.handelShowAddComment(false);
  };

  const handleCreateComment = () => {
    // `issueId` reaches this component as a plain string, while `saveCommentsData` takes the branded
    // id. Parse it rather than assert: a value that could not pass here would only be refused by the
    // server, and the composer keeps the draft instead of clearing it.
    const parsedIssueId = objectIdString.safeParse(props.issueId);
    if (!parsedIssueId.success) return;

    const imageTempKeys = extractTempKeysFromHtml(commentTitle);
    const processedTitle = replaceBase64WithTempKeys(
      commentTitle,
      (tempKey) => `${getBackendUrl()}/fileSystemServer/getCommentImage/${tempKey}`,
    );

    const savedData = {
      title: processedTitle,
      issueId: parsedIssueId.data,
      snapshot: commentSnapshot,
      viewpoint: commentViewpoint,
      bcfData,
      imageTempKeys,
    };
    dispatch(saveCommentsData(savedData));
    // KNOWN BUG, preserved verbatim: `handleAddComment` is optional at the only mount site and is
    // called unguarded here. Erased assertion; a guard would change behaviour.
    props.handleAddComment!(props.issueIndex);
    props.handelShowAddComment(false);
  };

  useEffect(() => {
    if (!props.pdfViewer && !props.issueIsStandalone) {
      handleTakeSnapshot();
    }
  }, []);

  return (
    <div className="flex flex-col pb-5">
      <div className="flex flex-row gap-2">
        <div className="flex">{getUserAvatar(userDetails.id)}</div>
        <div className="flex flex-row justify-between">
          <span className="flex flex-col justify-center text-xs">
            <span className="font-semibold">{`${createdByName.firstName} ${createdByName.lastName}`}</span>
          </span>
        </div>
      </div>
      <div className="flex w-full flex-col gap-1">
        <div className="mainComment">
          <SimpleEditor
            onChange={handleCommentDescriptionChange}
            value={commentTitle}
            editable={true}
            allowImageUpload={true}
          />
          <div className="flex justify-end gap-7 pt-5">
            <ButtonComponent
              buttonType={BUTTON_TYPES.GRADIENT_BUTTON}
              onClick={handleCreateComment}
              disabled={!postBtnEnabled}
            >
              <Text>{t("Post")}</Text>
            </ButtonComponent>
            <ButtonComponent buttonType={BUTTON_TYPES.BORDER_BUTTON} onClick={handleCancelComment}>
              <Text>{t("Cancel")}</Text>
            </ButtonComponent>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CommentsAdd;
